import { ApolloClient, Observable } from '@apollo/client';
import { HttpLink, ApolloLink, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { RestLink } from 'apollo-link-rest';
import * as Sentry from '@sentry/react';
import { reportNetworkError } from '../utils/networkErrorReporting';
import { reloadAppWithFreshAuth } from '../utils/authRefresh';

export const isUnauthorized = (error) => !!error && error.statusCode === 401;

export function exceedsMaximumCost(cost) {
  const {
    requestedQueryCost,
    actualQueryCost,
    throttleStatus: { maximumAvailable },
  } = cost;
  const requested = actualQueryCost || requestedQueryCost;

  return requested > maximumAvailable;
}

export function calculateDelayCost(cost) {
  const {
    requestedQueryCost,
    actualQueryCost,
    throttleStatus: { currentlyAvailable, restoreRate },
  } = cost;

  const requested = actualQueryCost || requestedQueryCost;
  const restoreAmount = Math.max(0, requested - currentlyAvailable);
  const msToWait = Math.ceil(restoreAmount / restoreRate) * 10000;

  return msToWait;
}

function delay(msToWait) {
  return new Observable((observer) => {
    let timer = setTimeout(() => {
      observer.complete();
    }, msToWait);

    return () => clearTimeout(timer);
  });
}

const createErrorLink = (graphQlUri, defaultRestUri) =>
  onError(({ graphQLErrors, networkError, forward, operation, response }) => {
    const operationName = operation && operation.operationName;

    if (graphQLErrors) {
      // GraphQL-level errors aren't network failures — keep them as breadcrumbs
      // so they attach to whatever exception captures next, instead of firing a
      // separate Sentry event each.
      graphQLErrors.forEach(({ message, locations, path }) => {
        Sentry.addBreadcrumb({
          category: 'graphql.error',
          level: 'error',
          message,
          data: { locations, path, operationName },
        });
      });
    }

    // An expired signed query (a tab left open) is expected: refresh the app instead of reporting it.
    if (isUnauthorized(networkError) && reloadAppWithFreshAuth()) return;

    if (networkError) {
      const ctx =(operation && operation.getContext && operation.getContext()) || {};
      const isRest =
        !!(ctx.restResponses && ctx.restResponses.length) ||
        ctx.isRestRequest === true;
      const url =
        (ctx.response && ctx.response.url) ||
        (ctx.restResponses && ctx.restResponses[0] && ctx.restResponses[0].url) ||
        (isRest ? defaultRestUri : graphQlUri) ||
        null;
      const method =
        (ctx.fetchOptions && ctx.fetchOptions.method) || (isRest ? 'GET' : 'POST');

      reportNetworkError(networkError, {
        url,
        method,
        source: isRest ? 'apollo_rest' : 'apollo_graphql',
        operationName,
      });
    }

    if (response && response.extensions) {
      const cost = response.extensions.cost;

      if (!cost) {
        // We require the cost extension to calculate the delay.
        // Fallback to ApolloClient's own error handler.
        return;
      }

      if (exceedsMaximumCost(cost)) {
        // Your query costs more than the maximum allowed cost.
        // Fallback to ApolloClient's own error handler.
        return;
      }

      const msToWait = calculateDelayCost(cost);
      operation.setContext({ retry: true, msToWait });

      return delay(msToWait).concat(forward(operation));
    }

    return;
  });

const restLink = (defaultRestUri, authQueryString, namedRestEndpoints = {}) => new RestLink({
    uri: defaultRestUri, // apollographql docs: "If you don't specify an endpoint in your query, the default endpoint (the one you specify in the uri option) will be used."
    endpoints: namedRestEndpoints,
    fetchOptions: {
      mode: "cors"
    },
    headers: {
      Authorization: authQueryString
    }
});

const httpLink = (graphQlUri, authQueryString) => new HttpLink({
    uri: graphQlUri,
    fetchOptions: {
      mode: "cors"
    },
    headers: {
      Authorization: authQueryString
    }
  });

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 5,
    // Replaying the same Authorization header can never turn a 401 into a success.
    retryIf: (error, _operation) => !!error && !isUnauthorized(error)
  }
});

export const createClient = (graphQlUri, defaultRestUri, authQueryString, namedRestEndpoints = {}) => new ApolloClient({
  link: ApolloLink.from([
    retryLink,
    createErrorLink(graphQlUri, defaultRestUri),
    restLink(defaultRestUri, authQueryString, namedRestEndpoints),
    httpLink(graphQlUri, authQueryString)
  ]),
  cache: new InMemoryCache()
});
